import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { setSession } from "../../auth/session.js";
import { AuthLayout, Button, ErrorNotice, Field, Input } from "../../components/form.js";
import { LegalConsent } from "../../components/legal-consent.js";

export function AcceptInvitePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = useSearch({ from: "/public/accept-invite" });
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");

  const preview = useQuery({
    queryKey: ["invites", "preview", token],
    queryFn: () => api.invites.preview(token),
    enabled: token.length > 0,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.invites.accept(token, { displayName, password }),
    onSuccess: (session) => {
      setSession(queryClient, session);
      void i18n.changeLanguage(session.user.locale);
      void navigate({ to: "/mail" });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    accept.mutate();
  };

  if (!token || preview.isError) {
    return (
      <AuthLayout title={t("auth.invite.invalidTitle")} subtitle={t("auth.invite.invalidSubtitle")}>
        <Link to="/login" className="text-sm text-accent underline-offset-2 hover:underline">
          {t("auth.signup.signIn")}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.invite.title")}
      subtitle={
        preview.data
          ? t("auth.invite.subtitle", {
              tenant: preview.data.tenantName,
              role: t(`roles.${preview.data.role}`),
            })
          : t("common.loading")
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("auth.fields.email")}>
          <Input type="email" value={preview.data?.email ?? ""} readOnly disabled />
        </Field>
        <Field label={t("auth.fields.displayName")}>
          <Input
            required
            autoComplete="name"
            maxLength={200}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.password")} hint={t("auth.fields.passwordHint")}>
          <Input
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <ErrorNotice error={accept.error} />
        <Button type="submit" disabled={accept.isPending || !preview.data}>
          {accept.isPending ? t("common.loading") : t("auth.invite.submit")}
        </Button>
        <LegalConsent i18nKey="auth.invite.agree" />
      </form>
    </AuthLayout>
  );
}
